/**
 * CrystallizationCelebration — тёплый 1.8 sec overlay на каждой
 * кристаллизации. Doc 28 §F: «после кристаллизации никакого специального
 * celebration — это momentous акт, нужен emphasis».
 *
 * Подписывается на `state.stats.totalCrystallizations` через useRef,
 * на инкремент → setVisible(true), AnimatePresence управляет
 * fade-in/out, авто-dismiss через 1.8 sec.
 *
 * Текст содержит порядковый номер кристаллизации («твоя 5-я память»)
 * + сколько до следующего milestone-артефакта в кольцах
 * (10/25/50/100). Игрок получает контекст «зачем».
 *
 * Wire: mount в App.tsx один раз. Renders null когда не активно.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import styles from './CrystallizationCelebration.module.css';

/** Пороги артефактов в кольцах (см. visual-artifacts/catalog.ts). */
const RING_MILESTONES = [1, 10, 25, 50, 100] as const;

interface Payload {
  readonly count: number;
  readonly gained: number;
  readonly nextRingAt: number | null;
}

function nextRing(count: number): number | null {
  for (const m of RING_MILESTONES) {
    if (count < m) return m;
  }
  return null;
}

export function CrystallizationCelebration(): JSX.Element {
  const totalCrystallizations = useGameStore((s) => s.state.stats.totalCrystallizations);
  // 2026-05-26 BUG FIX: «+1 духобалл» был hardcoded в render — игрок при
  // получении 3/5/10 духобаллов всё равно видел «+1». Теперь tracking
  // spirit_balls и compute delta = реальный gained.
  const spiritBalls = useGameStore((s) => s.state.resources.spirit_balls);
  const prevRef = useRef(totalCrystallizations);
  const prevSpiritBallsRef = useRef(spiritBalls);
  const [payload, setPayload] = useState<Payload | null>(null);

  useEffect(() => {
    if (totalCrystallizations <= prevRef.current) {
      prevRef.current = totalCrystallizations;
      prevSpiritBallsRef.current = spiritBalls;
      return undefined;
    }
    // Crystallization just happened. Diff spirit_balls = gained (includes
    // crystallizationBonus от upgrades, поэтому показывает true value).
    const gained = Math.max(1, spiritBalls - prevSpiritBallsRef.current);
    prevRef.current = totalCrystallizations;
    prevSpiritBallsRef.current = spiritBalls;
    setPayload({
      count: totalCrystallizations,
      gained,
      nextRingAt: nextRing(totalCrystallizations),
    });
    const tid = window.setTimeout(() => {
      setPayload(null);
    }, 1800);
    return () => {
      window.clearTimeout(tid);
    };
  }, [totalCrystallizations, spiritBalls]);

  return (
    <AnimatePresence>
      {payload !== null && (
        <motion.div
          className={styles.wrap}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
          aria-hidden
        >
          {/* 2026-05-26 polish: spring physics с лёгким overshoot вместо
              простого ease-out. Карточка делает «pop» (стиль ритуального
              появления), а не просто всплывает. stiffness/damping tuned
              для warm bounce без агрессии. exit остаётся ease — выходит
              мягко. */}
          <motion.div
            className={styles.card}
            initial={{ scale: 0.8, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.94, y: -6, opacity: 0 }}
            transition={{
              scale: { type: 'spring', stiffness: 320, damping: 14 },
              y: { type: 'spring', stiffness: 320, damping: 14 },
              opacity: { duration: 0.3, ease: 'easeOut' },
            }}
          >
            <p className={styles.preface}>кристаллизация</p>
            <p className={styles.amount}>
              +{payload.gained.toString()} {spiritBallWord(payload.gained)}
            </p>
            <p className={styles.context}>
              {ordinal(payload.count)} память
              {payload.nextRingAt !== null
                ? ` · до кольца ${payload.nextRingAt.toString()}: ${(payload.nextRingAt - payload.count).toString()}`
                : ''}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Простое порядковое имя на русском (для 1/2/3/4/5/...). */
function ordinal(n: number): string {
  const map: Record<number, string> = {
    1: 'первая',
    2: 'вторая',
    3: 'третья',
    4: 'четвёртая',
    5: 'пятая',
    10: 'десятая',
    25: 'двадцать пятая',
    50: 'пятидесятая',
    100: 'сотая',
  };
  return map[n] ?? `${n.toString()}-я`;
}

/**
 * Русская плюрализация для «духобалл». В Русском 3 формы:
 *   1 духобалл — nominative singular
 *   2-4 духобалла — genitive singular
 *   5+ духобаллов — genitive plural
 * Исключения: 11-14 → plural, 21 → singular, и т.д.
 */
function spiritBallWord(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return 'духобаллов';
  const last = n % 10;
  if (last === 1) return 'духобалл';
  if (last >= 2 && last <= 4) return 'духобалла';
  return 'духобаллов';
}
